import { db } from './firebase'
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where
} from 'firebase/firestore';

/**
 * Generate a random 6-character club code
 */
const generateClubCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Club Service
 * Handles all club-related operations with Firestore
 */
const clubService = {
  /**
   * Create a new club
   * @param {string} userId - User ID of the creator
   * @param {string} name - Club name
   * @param {string} location - Club location
   * @param {string} status - Initial status of the club (default: 'approved')
   * @returns {Promise<{success: boolean, id?: string, club?: Object, error?: string}>}
   */
  async createClub(userId, name, location, status = 'approved', adminEmail = null, adminName = null) {
    try {
      console.log('🔵 clubService: Creating club:', { userId, name, location, status });

      // Check if club name already exists
      const clubsRef = collection(db, 'club');
      const nameQuery = query(clubsRef, where('name', '==', name));
      const existingClubs = await getDocs(nameQuery);

      if (!existingClubs.empty) {
        console.log('⚠️ clubService: Club name already exists:', name);
        return {
          success: false,
          error: `A club named "${name}" already exists. Please choose a different name.`
        };
      }

      // Generate unique club code
      let code = generateClubCode();

      // Ensure club code is unique (very unlikely to collide, but check anyway)
      let codeExists = true;
      let attempts = 0;
      while (codeExists && attempts < 10) {
        const codeQuery = query(clubsRef, where('code', '==', code));
        const codeSnapshot = await getDocs(codeQuery);

        if (codeSnapshot.empty) {
          codeExists = false;
        } else {
          code = generateClubCode();
          attempts++;
        }
      }

      if (codeExists) {
        console.error('❌ clubService: Could not generate unique club code');
        return { success: false, error: 'Could not generate unique club code. Please try again.' };
      }

      const clubData = {
        name: name,
        location: location,
        code: code,
        createdBy: userId,
        adminId: userId,
        adminEmail: adminEmail,
        adminName: adminName,
        admins: [userId],
        teams: [],
        tournaments: [],
        status: status,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('🔵 clubService: Club data to save:', clubData);

      const docRef = await addDoc(clubsRef, clubData);

      console.log('✅ clubService: Club created with ID:', docRef.id);

      return {
        success: true,
        id: docRef.id,
        club: {
          id: docRef.id,
          ...clubData
        }
      };
    } catch (error) {
      console.error('❌ clubService: Error creating club:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all clubs
   * @returns {Promise<{success: boolean, clubs?: Array, error?: string}>}
   */
  async getAllClubs() {
    try {
      console.log('🔵 clubService: Fetching all clubs...');

      const clubsRef = collection(db, 'club');
      const snapshot = await getDocs(clubsRef);

      const clubs = [];
      snapshot.forEach((doc) => {
        clubs.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log('✅ clubService: Found', clubs.length, 'clubs');
      return { success: true, clubs };
    } catch (error) {
      console.error('❌ clubService: Error getting clubs:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get club by ID
   * @param {string} clubId - Club document ID
   * @returns {Promise<{success: boolean, club?: Object, error?: string}>}
   */
  async getClubById(clubId) {
    try {
      console.log('🔵 clubService: Fetching club:', clubId);

      const clubRef = doc(db, 'club', clubId);
      const clubDoc = await getDoc(clubRef);

      if (clubDoc.exists()) {
        console.log('✅ clubService: Club found');
        return {
          success: true,
          club: { id: clubDoc.id, ...clubDoc.data() }
        };
      }

      console.log('⚠️ clubService: Club not found');
      return { success: false, error: 'Club not found' };
    } catch (error) {
      console.error('❌ clubService: Error getting club:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update club information
   * @param {string} clubId - Club document ID
   * @param {Object} clubData - Updated club information
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async updateClub(clubId, clubData) {
    try {
      console.log('🔵 clubService: Updating club:', clubId);

      const clubRef = doc(db, 'club', clubId);
      await updateDoc(clubRef, {
        ...clubData,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ clubService: Club updated successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ clubService: Error updating club:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Approve a pending club
   * @param {string} clubId - Club document ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async approveClub(clubId) {
    try {
      console.log('🔵 clubService: Approving club:', clubId);

      const clubRef = doc(db, 'club', clubId);
      await updateDoc(clubRef, {
        status: 'approved',
        updatedAt: new Date().toISOString()
      });

      console.log('✅ clubService: Club approved successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ clubService: Error approving club:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete a club
   * @param {string} clubId - Club document ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteClub(clubId) {
    try {
      console.log('🔵 clubService: Deleting club:', clubId);

      const clubRef = doc(db, 'club', clubId);
      await deleteDoc(clubRef);

      console.log('✅ clubService: Club deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ clubService: Error deleting club:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Find club by club code
   * @param {string} code - Club code
   * @returns {Promise<{success: boolean, club?: Object, error?: string}>}
   */
  async findClubByCode(code) {
    try {
      console.log('🔵 clubService: Finding club by code:', code);

      const clubsRef = collection(db, 'club');
      const q = query(clubsRef, where('code', '==', code));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log('⚠️ clubService: Club not found with code:', code);
        return { success: false, error: 'Club not found' };
      }

      const clubDoc = snapshot.docs[0];
      console.log('✅ clubService: Club found:', clubDoc.data().name);
      return {
        success: true,
        club: { id: clubDoc.id, ...clubDoc.data() }
      };
    } catch (error) {
      console.error('❌ clubService: Error finding club by code:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get clubs by admin email
   * @param {string} adminEmail - Admin's email address
   * @returns {Promise<{success: boolean, clubs?: Array, error?: string}>}
   */
  async getClubsByAdmin(adminEmail) {
    try {
      console.log('🔵 clubService: Fetching clubs for admin:', adminEmail);

      const clubsRef = collection(db, 'club');
      const q = query(clubsRef, where('adminEmail', '==', adminEmail));
      const snapshot = await getDocs(q);

      const clubs = [];
      snapshot.forEach((doc) => {
        clubs.push({ id: doc.id, ...doc.data() });
      });

      console.log('✅ clubService: Found', clubs.length, 'clubs for admin');
      return { success: true, clubs };
    } catch (error) {
      console.error('❌ clubService: Error getting clubs by admin:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get clubs by admin userId (for useClub hook)
   * @param {string} userId - Admin's user ID
   * @returns {Promise<{success: boolean, clubs?: Array, error?: string}>}
   */
  async getAdminClubs(userId) {
    try {
      console.log('🔵 clubService: Fetching admin clubs for userId:', userId);

      // First, get the user's data to check their role and club
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        console.log('⚠️ clubService: User not found');
        return { success: true, clubs: [] };
      }

      const userData = userSnap.data();
      console.log('🔵 clubService: User data:', userData);

      // If user is super-admin, return all clubs
      if (userData.role === 'super-admin') {
        console.log('🔵 clubService: User is super-admin, fetching all clubs');
        const clubsRef = collection(db, 'club');
        const snapshot = await getDocs(clubsRef);

        const clubs = [];
        snapshot.forEach((doc) => {
          clubs.push({ id: doc.id, ...doc.data() });
        });

        console.log('✅ clubService: Found', clubs.length, 'clubs for super-admin');
        return { success: true, clubs };
      }

      // If user is regular admin, treasurer, or auditor, get their club from clubInfo
      const userRole = userData.role;
      const additionalRoles = userData.clubInfo?.additionalRoles || userData.additionalRoles || [];
      const hasPrivilegedRole = userRole === 'admin' ||
        userRole === 'treasurer' ||
        userRole === 'auditor' ||
        additionalRoles.includes('treasurer') ||
        additionalRoles.includes('auditor');
        
      const clubsMap = new Map();

      if (hasPrivilegedRole && userData.clubInfo?.clubId) {
        console.log('🔵 clubService: User has privileged role, fetching their club:', userData.clubInfo.clubId);

        const clubRef = doc(db, 'club', userData.clubInfo.clubId);
        const clubSnap = await getDoc(clubRef);

        if (clubSnap.exists()) {
          const club = { id: clubSnap.id, ...clubSnap.data() };
          clubsMap.set(club.id, club);
        }
      }

      // Also add any clubs from joinedClubs where role is admin
      if (userData.joinedClubs) {
         for (const [jClubId, jClubData] of Object.entries(userData.joinedClubs)) {
             if (jClubData.role === 'admin' || jClubData.role === 'treasurer' || jClubData.role === 'auditor') {
                 if (!clubsMap.has(jClubId)) {
                     const clubRef = doc(db, 'club', jClubId);
                     const clubSnap = await getDoc(clubRef);
                     if (clubSnap.exists()) {
                         clubsMap.set(jClubId, { id: clubSnap.id, ...clubSnap.data() });
                     }
                 }
             }
         }
      }

      // Fallback: Check if user created any clubs or is an admin directly on the club document
      const clubsCollectionRef = collection(db, 'club');
      const fallbackSnapshot = await getDocs(clubsCollectionRef);

      fallbackSnapshot.forEach((doc) => {
        const clubData = doc.data();
        if (clubData.adminId === userId ||
          clubData.createdBy === userId ||
          clubData.userId === userId ||
          (clubData.admins && clubData.admins.includes(userId))) {
          if (!clubsMap.has(doc.id)) {
             clubsMap.set(doc.id, { id: doc.id, ...clubData });
          }
        }
      });

      const clubs = Array.from(clubsMap.values());
      console.log('✅ clubService: Found', clubs.length, 'clubs for userId:', userId);
      return { success: true, clubs };
    } catch (error) {
      console.error('❌ clubService: Error getting admin clubs:', error);
      return { success: false, error: error.message };
    }
  }
};

export default clubService;